import { Profile, ProfileFormData } from '../types';

// API base URL - should be configured based on environment
const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL || 'https://obta-api.trade.workers.dev'}/api`;

// API token for development
const API_TOKEN = 'obta-production-api-token-2025';

// Helper function to get the authentication token
// TODO: Implement proper token retrieval when auth is fully implemented

// Helper function to handle API errors
const handleApiError = (error: any): never => {
  console.error('API Error:', error);
  throw new Error(error.message || 'An error occurred while communicating with the API');
};

// Define the ProfileHistory interface
export interface ProfileHistoryEntry {
  id: string;
  timestamp: number;
  user: {
    id: string;
    username: string;
    full_name: string;
  };
  action: string;
  changes: string[];
  raw_details: {
    previous: any;
    changes: any;
  };
}

export interface ProfileHistoryResponse {
  profile_id: string;
  history: ProfileHistoryEntry[];
}

export const ProfilesService = {
  /**
   * Get all profiles with optional filtering
   */
  async getProfiles(positionType?: string, showActive?: boolean): Promise<Profile[]> {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (positionType) params.append('position_type', positionType);
      if (showActive !== undefined) params.append('show_active', showActive.toString());
      
      // Make API request
      const response = await fetch(`${API_BASE_URL}/profiles?${params.toString()}`, {
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch profiles: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.results;
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  /**
   * Get a single profile by ID
   */
  async getProfileById(id: string): Promise<Profile> {
    try {
      const response = await fetch(`${API_BASE_URL}/profiles/${id}`, {
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch profile: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  /**
   * Get profile history
   */
  async getProfileHistory(id: string): Promise<ProfileHistoryResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/profiles/${id}/history`, {
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch profile history: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  /**
   * Create a new profile
   */
  async createProfile(profileData: ProfileFormData): Promise<Profile> {
    try {
      const response = await fetch(`${API_BASE_URL}/profiles`, {
        method: 'POST',
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profileData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to create profile: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  /**
   * Update an existing profile
   */
  async updateProfile(id: string, profileData: Partial<ProfileFormData>): Promise<Profile> {
    try {
      const response = await fetch(`${API_BASE_URL}/profiles/${id}`, {
        method: 'PATCH',
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profileData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update profile: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  /**
   * Delete a profile
   */
  async deleteProfile(id: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/profiles/${id}`, {
        method: 'DELETE',
        headers: {
          'X-API-Token': API_TOKEN
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete profile: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      return handleApiError(error);
    }
  }
};

export default ProfilesService;
